import { useCallback, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from 'hooks/auth/useAuth'
import {
  clearLastActivityTimestamp,
  getLastActivityTimestamp,
  isIdleExpired,
  setLastActivityTimestamp,
} from '@/core/activity/lastActivity'

const ACTIVITY_THROTTLE_MS = 2000
const CHECK_EVERY_MS = 15000
const ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart', 'click'] as const

export function useIdleSessionTimeout() {
  const { currentUser, logout } = useAuth()
  const navigate = useNavigate()
  const throttleTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const markActivity = useCallback(() => {
    if (throttleTimeoutRef.current !== null) {
      return
    }
    setLastActivityTimestamp(Date.now())
    throttleTimeoutRef.current = setTimeout(() => {
      throttleTimeoutRef.current = null
    }, ACTIVITY_THROTTLE_MS)
  }, [])

  useEffect(() => {
    if (!currentUser) {
      clearLastActivityTimestamp()
      return
    }

    if (getLastActivityTimestamp() === undefined) {
      setLastActivityTimestamp(Date.now())
    }

    const onActivity = () => {
      markActivity()
    }
    ACTIVITY_EVENTS.forEach(event => {
      window.addEventListener(event, onActivity, { passive: true })
    })

    const expireIfIdle = async () => {
      if (!isIdleExpired()) {
        return
      }
      clearLastActivityTimestamp()
      await logout()
      navigate('/session-expired', { replace: true })
    }

    void expireIfIdle()
    const intervalId = window.setInterval(() => {
      void expireIfIdle()
    }, CHECK_EVERY_MS)

    return () => {
      ACTIVITY_EVENTS.forEach(event => {
        window.removeEventListener(event, onActivity)
      })
      window.clearInterval(intervalId)
      if (throttleTimeoutRef.current) {
        clearTimeout(throttleTimeoutRef.current)
        throttleTimeoutRef.current = null
      }
    }
  }, [currentUser, logout, markActivity, navigate])
}
